<template>
  <div>
    <v-container fluid>
      <v-row align="center">
        <v-col class="d-flex" cols="12" sm="2">
          <v-select
            v-model="filter.selection.providerStatus"
            :items="filter.options.providerStatuses"
            label="Provider Status"
            :clearable="true"
          ></v-select>
        </v-col>
        <span class="caption">AND</span>
        <v-col class="d-flex" cols="12" sm="2">
          <v-select
            v-model="filter.selection.patientStatus"
            :items="filter.options.patientStatuses"
            label="Patient Status"
            :clearable="true"
          ></v-select>
        </v-col>
        <span class="caption">AND</span>
        <v-col class="d-flex" cols="12" sm="4">
          <v-text-field
            v-model="filter.search"
            label="Search"
            :persistent-hint="true"
            hint="Appointment ID, Order No."
          />
        </v-col>
      </v-row>
    </v-container>
    <v-data-table
      :headers="headers"
      :items="prescriptions"
      :options.sync="options"
      :server-items-length="totalPrescriptionCount"
      :loading="loading > 0"
      :footer-props="footerProps"
      :items-per-page="itemsPerPage"
      class="elevation-1"
    >
      <template v-slot:item.providerStatus="{ item }">
        <div>{{ item.providerStatus.status }}</div>
        <div v-if="item.providerStatus.reason">
          {{ 'Reason: ' + item.providerStatus.reason }}
        </div>
      </template>
      <!-- ../MedicineInfoDialog.vue editable dialog for prescription medicines ; 
        passes current prescription's medicines array into the dialog ; 
        passes prescription ID to identify which document to update ; 
        refreshes prescription list after medicines successfully updated-->
      <!-- Show button always renders regardless of prescription_details existence, allowing admins to overwrite edge cases -->
      <template v-slot:item.medicineInfo="{ item }">
        <div>
          <medicine-info-dialog
            :medicines="item.medicines || []"
            :prescription-id="item.prescriptionId"
            @success="getPrescriptions(fetchParameters)"
          />
        </div>
      </template>
      <template v-slot:item.comment="{ item }">
        <div v-if="item.comment">
          <comment-dialog :comment="item.comment" />
        </div>
        <div v-else>-</div>
      </template>
      <template v-slot:item.deliveryType="{ item }">
        <div v-if="item.deliveryType">{{ item.deliveryType }}</div>
        <div v-else>-</div>
      </template>
      <template v-slot:item.deliveryFee="{ item }">
        <div v-if="item.deliveryFee">{{ item.deliveryFee }}</div>
        <div v-else>-</div>
      </template>
      <template v-slot:item.trackingNumber="{ item }">
        <div v-if="item.trackingNumber">{{ item.trackingNumber }}</div>
        <div v-else>-</div>
      </template>
      <template v-slot:item.action="{ item }">
        <div v-if="item.providerStatus.status === 'PAID'" class="d-flex">
          <add-comment-dialog :epresOrder="item" />
          <add-out-for-delivery-dialog
            :epresOrder="item"
            :onSubmitted="
              () => {
                getPrescriptions(fetchParameters);
              }
            "
          />
          <add-tracking-number-dialog
            :epresOrder="item"
            :onSubmitted="
              () => {
                getPrescriptions(fetchParameters);
              }
            "
          />
          <label-dialog
            :medicines="item.medicines"
            :appointmentId="item.appointmentId"
            :order-number="item.orderNumber"
          />
        </div>
        <div v-else-if="item.providerStatus.status === 'OUT_FOR_DELIVERY'" class="d-flex">
          <add-comment-dialog :epresOrder="item" />
          <add-tracking-number-dialog
            :epresOrder="item"
            :onSubmitted="
              () => {
                getPrescriptions(fetchParameters);
              }
            "
          />
          <label-dialog
            :medicines="item.medicines"
            :appointmentId="item.appointmentId"
            :order-number="item.orderNumber"
          />
        </div>
        <div v-else>
          <add-comment-dialog :epresOrder="item" />
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import _ from 'lodash';
import PrescriptionService from '../../../services/prescription';
import MedicineInfoDialog from '../MedicineInfoDialog.vue';
import TrackingNumberDialog from './TrackingNumberDialog.vue';
import AddTrackingNumberDialog from './AddTrackingNumberDialog.vue';
import LabelDialog from './LabelMaker/LabelDialog.vue';
import AddCommentDialog from './AddCommentDialog.vue';
import AddOutForDeliveryDialog from './AddOutForDeliveryDialog.vue';
import CommentDialog from './CommentDialog.vue';

export default {
  components: {
    MedicineInfoDialog,
    TrackingNumberDialog,
    AddTrackingNumberDialog,
    AddCommentDialog,
    AddOutForDeliveryDialog,
    CommentDialog,
    LabelDialog,
  },
  name: 'PrescriptionList',
  data() {
    return {
      loading: 0,
      totalPrescriptionCount: 0,
      prescriptions: [],
      options: {
        page: 1,
        limit: 10,
        sortBy: ['orderNumber'],
        sortDesc: [true],
      },
      itemsPerPage: 10,
      footerProps: {
        'items-per-page-options': [10, 20, 30, 40, 50],
      },
      headers: [
        {
          text: 'Appointment ID',
          value: 'appointmentId',
          sortable: false,
        },
        {
          text: 'Order No.',
          value: 'orderNumber',
        },
        {
          text: 'User ID',
          value: 'userId',
        },
        {
          text: 'Provider submitted at',
          value: 'submittedAt',
        },
        {
          text: 'Provider status',
          value: 'providerStatus',
          sortable: false,
        },
        {
          text: 'Provider status updated at',
          value: 'providerStatusUpdatedAt',
        },
        {
          text: 'Patient status',
          value: 'patientStatus',
          sortable: false,
        },
        {
          text: 'Patient status updated at',
          value: 'patientStatusUpdatedAt',
        },
        {
          text: 'Patient paid at',
          value: 'patientPaidAt',
          sortable: false,
        },
        {
          text: 'Medicine info',
          value: 'medicineInfo',
          sortable: false,
        },
        {
          text: 'Total fee',
          value: 'medicineTotalFee',
          sortable: false,
        },
        {
          text: 'Comment',
          value: 'comment',
          sortable: false,
        },
        {
          text: 'Delivery Type',
          value: 'deliveryType',
          sortable: false,
        },
        {
          text: 'Delivery Fee',
          value: 'deliveryFee',
          sortable: false,
        },
        {
          text: 'Tracking number',
          value: 'trackingNumber',
          sortable: false,
        },
        {
          text: '',
          value: 'action',
          sortable: false,
        },
      ],
      filter: {
        search: '',
        selection: {
          providerStatus: undefined,
          patientStatus: undefined,
        },
        options: {
          providerStatuses: [],
          patientStatuses: [],
        },
      },
      ongoingRequest: Promise.resolve(),
    };
  },
  mounted() {
    this.getPrescriptions(this.fetchParameters);
    this.getFilterOptions();
  },
  methods: {
    async getFilterOptions() {
      this.loading += 1;
      try {
        const filterOptions = await PrescriptionService.getFilterOptions();
        this.filter.options.providerStatuses = filterOptions.providerStatuses;
        this.filter.options.patientStatuses = filterOptions.patientStatuses;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading -= 1;
      }
    },
    getPrescriptions: _.debounce(async function (fetchParameters) {
      this.loading += 1;
      try {
        await this.ongoingRequest;

        const request = PrescriptionService.paginate(fetchParameters);
        this.ongoingRequest = request;

        const paginatedPrescriptions = await request;
        this.prescriptions = paginatedPrescriptions.data;
        this.totalPrescriptionCount = paginatedPrescriptions.total;
      } catch (err) {
        console.error(err);
      } finally {
        this.loading -= 1;
      }
    }, 500),
  },
  computed: {
    fetchParameters() {
      const paramters = {
        page: this.options.page,
        limit: this.options.itemsPerPage,
        filter: {
          search: this.filter.search,
          providerStatus: this.filter.selection.providerStatus,
          patientStatus: this.filter.selection.patientStatus,
        },
      };

      // Multiple field sorting is not supported.
      if (this.options.sortBy && this.options.sortBy.length === 1) {
        paramters.sort = {
          by: this.options.sortBy[0],
          order: this.options.sortDesc[0] ? 'desc' : 'asc',
        };
      }

      return paramters;
    },
  },
  watch: {
    fetchParameters(params, prevParams) {
      if (!_.isEqual(params.filter, prevParams.filter) && params.page !== 1) {
        this.options.page = 1;
      } else {
        this.getPrescriptions(params);
      }
    },
  },
};
</script>
